import React, { memo } from 'react';
import { TableCell, Box, IconButton } from '@mui/material';
import { Edit, Delete, DragHandle } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import Menu from '../../../../../app/data/models/menu';
import StyledTableRow from '../../../../../app/components/StyledTableRow';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface Props {
  menu: Menu;
  index: number;
  loading: boolean;
  onMenuClick: (menu: Menu) => void;
  onEditClick: (menu: Menu) => void;
  onDeleteClick: (menuId: string) => void;
}

export const MenuTableRow: React.FC<Props> = memo(
  ({ menu, index, loading, onMenuClick, onEditClick, onDeleteClick }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: menu.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    return (
      <StyledTableRow
        ref={setNodeRef}
        style={style}
        {...attributes}
        onClick={() => onMenuClick(menu)}
        sx={{ cursor: 'pointer' }}
      >
        <TableCell {...listeners}>
          <DragHandle />
        </TableCell>
        <TableCell>{index + 1}</TableCell>
        <TableCell>{menu.title}</TableCell>
        <TableCell align="right">
          <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'flex-end' }}>
            <IconButton
              sx={{ p: 0.25, width: '36px', minWidth: '36px' }}
              onClick={(e) => {
                e.stopPropagation();
                onEditClick(menu);
              }}
              size="small"
            >
              <Edit />
            </IconButton>
            <LoadingButton
              sx={{ p: 0.25, width: '36px', minWidth: '36px' }}
              loading={loading}
              onClick={(e) => {
                e.stopPropagation();
                onDeleteClick(menu.id);
              }}
              color="error"
              size="small"
            >
              <Delete />
            </LoadingButton>
          </Box>
        </TableCell>
      </StyledTableRow>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.menu === nextProps.menu &&
      prevProps.loading === nextProps.loading
    );
  }
);

MenuTableRow.displayName = 'MenuTableRow';
